import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscriptionPlans, getIsSubscribed } from '../reducers/Subscription/selectors';
import { fetchSubscriptionPlans, createOrder, openRazorpay, verifyPayment } from '../reducers/Subscription/actions';
import { CLEAR_ORDER_DATA } from '../reducers/Subscription/constants';
import { showErrorMsg } from './notifications';
import { SUBSCRIPTION_GATE_EVENTS } from './subscriptionGate';
import analytics from './analytics';
import SubscriptionPopup from '../app/main/helperComponents/SubscriptionPopup';

const SubscriptionGateProvider = ({ children }) => {
  const dispatch = useDispatch();
  const subscriptionPlans = useSelector(getSubscriptionPlans());
  const isSubscribed = useSelector(getIsSubscribed());
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [entryPoint, setEntryPoint] = useState(null);
  
  // Use a ref to store the pending action to avoid React state execution issues
  const pendingActionRef = useRef(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const isModalOpenRef = useRef(false);

  // Auto-select the first plan if only one is available (same as subscription page)
  useEffect(() => {
    if (subscriptionPlans && subscriptionPlans.length === 1 && !selectedPlan) {
      setSelectedPlan(subscriptionPlans[0]);
    }
  }, [subscriptionPlans, selectedPlan]);

  const onClose = useCallback(() => {
    setIsModalOpen(false);
    isModalOpenRef.current = false;
    pendingActionRef.current = null;
    setEntryPoint(null);
    setSelectedPlan(null);
    setLoading(false);
    // Clear any pending order data to prevent loading state issues
    dispatch({ type: CLEAR_ORDER_DATA });
  }, [dispatch]);

  const handleSubscriptionSuccess = useCallback(() => {
    // Track successful subscription completion
    analytics.trackPlatformEvent('subscription_popup_completed', {
      had_plan_selected: !!selectedPlan,
      is_subscribed: isSubscribed,
      close_reason: 'payment_successful',
      plan_selected: selectedPlan ? {
        plan_text: selectedPlan.text,
        plan_final_amount: selectedPlan.final_amount,
        plan_discount: selectedPlan.discount
      } : null
    });
    
    if (pendingActionRef.current && typeof pendingActionRef.current === 'function') {
      try {
        pendingActionRef.current();
        pendingActionRef.current = null; // Clear after execution
      } catch (error) {
        console.error('Error executing pending action:', error);
      }
    }
    onClose();
  }, [onClose, selectedPlan, isSubscribed]);

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      showErrorMsg('Please select a subscription plan');
      return;
    }

    if (!isModalOpenRef.current) return; // Don't proceed if modal is closed
    
    setLoading(true);
    try {
      const orderData = await dispatch(createOrder(selectedPlan.id));
      if (orderData && isModalOpenRef.current) { // Check if modal is still open
        dispatch(openRazorpay(orderData, 
          async (paymentResponse) => {
            // Success callback - verify payment with backend
            try {
              const verified = await dispatch(verifyPayment({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              }));
              
              if (verified && isModalOpenRef.current) {
                setLoading(false);
                handleSubscriptionSuccess();
              } else if (isModalOpenRef.current) {
                setLoading(false);
                showErrorMsg('Payment verification failed. Please contact support.');
              }
            } catch (error) {
              if (isModalOpenRef.current) {
                setLoading(false);
                showErrorMsg('Payment verification failed. Please contact support.');
              }
            }
          },
          () => {
            // Failure callback
            if (isModalOpenRef.current) {
              setLoading(false);
            }
          }
        ));
      } else if (isModalOpenRef.current) {
        setLoading(false);
      }
    } catch (error) {
      if (isModalOpenRef.current) {
        setLoading(false);
        showErrorMsg('Failed to create subscription order');
      }
    }
  };

  useEffect(() => {
    const handleSubscriptionRequired = (event) => {
      const { pendingAction: action, entry_point } = event.detail || {};
      
      // Emit only on transition closed → open
      if (!isModalOpen) {
        try {
          analytics.trackPlatformEvent('view_subscription_modal', {
            source: (event.detail && event.detail.source) || 'unknown',
            entry_point: entry_point || 'unknown',
            is_subscribed: isSubscribed,
            prev_path: (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : ''
          });
        } catch (_) {}
      }
      
      // Store the action in a ref to prevent immediate execution
      pendingActionRef.current = action;
      setEntryPoint(entry_point);
      
      // Only open modal if it's not already open
      if (!isModalOpen) {
        setIsModalOpen(true);
        isModalOpenRef.current = true;
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(SUBSCRIPTION_GATE_EVENTS.SUBSCRIPTION_REQUIRED_EVENT, handleSubscriptionRequired);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(SUBSCRIPTION_GATE_EVENTS.SUBSCRIPTION_REQUIRED_EVENT, handleSubscriptionRequired);
      }
    };
  }, [isModalOpen, isSubscribed]);


  // Fetch subscription plans when modal opens
  useEffect(() => {
    if (isModalOpen && subscriptionPlans.length === 0) {
      dispatch(fetchSubscriptionPlans());
    }
  }, [isModalOpen, subscriptionPlans.length, dispatch]);

  // If user becomes subscribed while modal is open, automatically complete the gate
  useEffect(() => {
    if (isModalOpen && isSubscribed) {
      handleSubscriptionSuccess();
    }
  }, [isModalOpen, isSubscribed, handleSubscriptionSuccess]);

  return (
    <>
      {children}
      <SubscriptionPopup
        isOpen={isModalOpen}
        onClose={onClose}
        onSuccess={handleSubscriptionSuccess}
        subscriptionPlans={subscriptionPlans}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        loading={loading}
        onSubscribe={handleSubscribe}
        isSubscribed={isSubscribed}
      />
    </>
  );
};

export default SubscriptionGateProvider;
